import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Autocomplete,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Info as InfoIcon } from '@mui/icons-material';
import { useCatalogService } from '../../services/catalogService';
import { useAddressService } from '../../services/addressService';
import { useAuth } from '../../context/AuthContext';
import { usePhoneCallService } from '../../services/phoneCallService';
import { UserRole } from '../../types/auth';
import type { StateDTO, MunicipalityDTO, CityDTO } from '../../types/address';
import type { SpecialistStatusHistoryEntry, TemporaryAddressDTO } from '../../types/phoneCapture';
import SpecialistContactForm from './SpecialistContactForm';
import AutocompleteField from '../common/AutocompleteField';

// Tipos locales (respetando tu modelo de dominio)
interface Specialist {
  specialistId: number;
  fullName: string;
  productArea: string;
  professionalLicense?: string;
  notes?: string;
  active: boolean; // backend usa 'active'
  isActive?: boolean;
  statusChangeReason?: string | null;
  statusChangedAt?: string | null;
  statusChangedBy?: {
    id: number;
    username?: string;
    fullName?: string;
    email?: string;
  } | null;
  createdAt: string;
  modalities: Modality[];
  specialties: Specialty[];
  serviceAreas: ServiceArea[];
}

interface Modality {
  modalityId: number;
  code: string;
  name: string;
  notes?: string;
}

interface Specialty {
  specialtyId: number;
  name: string;
}

interface ServiceArea {
  serviceId: number;
  name: string;
}

interface SpecialistContact {
  contactId?: number;
  specialistId?: number;
  contactType: string;
  contactValue: string;
  isPrimary: boolean;
  notes?: string;
  isActive?: boolean;
  createdAt?: string;
}

interface AddressFormData {
  id?: number;
  cveEnt: string; // clave de estado
  cveMun: string; // clave de municipio
  cveLoc: string; // clave de localidad/ciudad
  cp: string; // código postal
  settlementId?: number; // asentamiento (opcional si lo manejas después)
  calle: string;
  numExterior: string;
  numInterior: string;
  referencias: string;
  isPrimary: boolean;
}

interface SpecialtyFormItem {
  specialtyId?: number | null;
  name: string;
  isNew?: boolean;
}

interface TemporaryAddressFormData {
  id?: number; // Para edición (si tiene ID, es existente)
  direccion: string;
  horario?: string;
  notas?: string;
}

interface SpecialistFormData {
  fullName: string;
  productArea: string;
  coverageArea: any | null; // ✅ NUEVO: Área de cobertura geográfica
  notes: string;
  isActive: boolean; // para UI; backend usa 'active'
  modalityCodes: string[];
  specialtyNames: string[]; // Mantener para compatibilidad con el input
  specialties: SpecialtyFormItem[]; // Nueva estructura con IDs
  contacts: SpecialistContact[];
  addresses: AddressFormData[];
  temporaryAddresses: TemporaryAddressFormData[]; // ✅ NUEVO: Direcciones temporales
}

interface SpecialistModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (formData: SpecialistFormData) => Promise<void> | void;
  onStatusChanged?: (specialist: Specialist) => void;
  editingSpecialist?: Specialist | null;
  loading?: boolean;
}

const resolveSpecialistActive = (specialist?: Specialist | null): boolean =>
  specialist?.active ?? specialist?.isActive ?? true;

// Componente TabPanel para los tabs
function TabPanel(props: { children?: React.ReactNode; value: number; index: number; }) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`specialist-tabpanel-${index}`}
      aria-labelledby={`specialist-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const SpecialistModal: React.FC<SpecialistModalProps> = ({
  open,
  onClose,
  onSave,
  onStatusChanged,
  editingSpecialist,
  loading = false,
}) => {
  // 🔐 Auth
  const { jwtToken, hasAnyRole } = useAuth();
  const {
    getStates,
    searchMunicipalitiesByState,
    searchCitiesByStateAndMunicipality
  } = useAddressService();
  const { updateSpecialistStatus, getSpecialistStatusHistory } = usePhoneCallService();
  const canChangeStatus = hasAnyRole([UserRole.ADMIN, UserRole.SUPERVISOR]);
  const currentActive = resolveSpecialistActive(editingSpecialist);

  // 📚 Catálogos
  const { getAllCatalogs, getSpecialties, getCoverageAreas } = useCatalogService();
  const getAllCatalogsRef = useRef(getAllCatalogs);
  const getSpecialtiesRef = useRef(getSpecialties);
  const getCoverageAreasRef = useRef(getCoverageAreas);
  
  useEffect(() => {
    getAllCatalogsRef.current = getAllCatalogs;
    getSpecialtiesRef.current = getSpecialties;
    getCoverageAreasRef.current = getCoverageAreas;
  }, [getAllCatalogs, getSpecialties, getCoverageAreas]);

  const [catalogs, setCatalogs] = useState<any>({});
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [specialties, setSpecialties] = useState<any[]>([]); // ✅ NUEVO: Especialidades cargadas del API
  const [coverageAreas, setCoverageAreas] = useState<any[]>([]); // ✅ NUEVO: Áreas de cobertura

  // 🗺️ Direcciones
  const [states, setStates] = useState<StateDTO[]>([]);
  const [municipalities, setMunicipalities] = useState<MunicipalityDTO[]>([]);
  const [cities, setCities] = useState<CityDTO[]>([]);
  const [addressLoading, setAddressLoading] = useState({ states: false, municipalities: false, cities: false });

  // Flag para evitar recargas/efectos duplicados
  const hasLoadedRef = useRef(false);

  // Estado para tabs
  const [tabValue, setTabValue] = useState(0);

  // Estatus (PATCH + historial)
  const [statusReasonDialogOpen, setStatusReasonDialogOpen] = useState(false);
  const [pendingStatusActive, setPendingStatusActive] = useState<boolean | null>(null);
  const [statusReason, setStatusReason] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [statusHistory, setStatusHistory] = useState<SpecialistStatusHistoryEntry[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // 📝 Formulario
  const [formData, setFormData] = useState<SpecialistFormData>({
    fullName: '',
    productArea: '',
    coverageArea: null, // ✅ NUEVO: Área de cobertura
    notes: '',
    isActive: true,
    modalityCodes: [],
    specialtyNames: [],
    specialties: [],
    contacts: [],
    addresses: [],
    temporaryAddresses: [], // ✅ NUEVO: Direcciones temporales
  });

  // 🛡️ Fix anti-ciclo: solo cargar una vez por apertura (y si catálogos están vacíos)
  useEffect(() => {
    if (open && !hasLoadedRef.current && Object.keys(catalogs).length === 0) {
      hasLoadedRef.current = true;
      let isMounted = true;

      const loadData = async () => {
        try {
          setCatalogsLoading(true);
          
          // Cargar catálogos generales
          const catalogsData = await getAllCatalogsRef.current();
          if (isMounted) setCatalogs(catalogsData || {});
          
          // ✅ CARGAR ESPECIALIDADES SEPARADAMENTE según especificación GET /api/specialties
          try {
            console.log('📋 Cargando especialidades desde GET /api/specialties...');
            const specialtiesData = await getSpecialtiesRef.current();
            console.log('📋 Especialidades cargadas:', {
              count: specialtiesData?.length || 0,
              data: specialtiesData,
              sample: specialtiesData?.[0]
            });
            if (isMounted) setSpecialties(specialtiesData || []);
          } catch (error) {
            console.error('❌ Error cargando especialidades:', error);
            if (isMounted) setSpecialties([]);
          }

          // ✅ CARGAR ÁREAS DE COBERTURA según especificación GET /api/coverage-areas
          try {
            console.log('📍 Cargando áreas de cobertura desde GET /api/coverage-areas...');
            const coverageAreasData = await getCoverageAreasRef.current();
            console.log('📍 Áreas de cobertura cargadas:', {
              count: coverageAreasData?.length || 0,
              data: coverageAreasData,
              sample: coverageAreasData?.[0]
            });
            if (isMounted) setCoverageAreas(coverageAreasData || []);
          } catch (error) {
            console.error('❌ Error cargando áreas de cobertura:', error);
            if (isMounted) setCoverageAreas([]);
          }
        } catch (error) {
          console.error('❌ Error cargando catálogos:', error);
        } finally {
          if (isMounted) setCatalogsLoading(false);
        }

        if (jwtToken) {
          try {
            if (isMounted) setAddressLoading(prev => ({ ...prev, states: true }));
            const response = await getStates();
            if (response.ok && isMounted) {
              setStates(response.data);
            }
          } catch (error) {
            console.error('❌ Error cargando estados:', error);
          } finally {
            if (isMounted) setAddressLoading(prev => ({ ...prev, states: false }));
          }
        }
      };

      loadData();

      return () => {
        isMounted = false;
      };
    }

    // Reset al cerrar para reintentar en siguiente apertura si hace falta
    if (!open) {
      hasLoadedRef.current = false;
      setTabValue(0);
      setStatusReasonDialogOpen(false);
      setStatusError('');
      setStatusReason('');
      setPendingStatusActive(null);
    }
  }, [open, jwtToken]);

  // Inicializar formulario según modo (nuevo/edición)
  useEffect(() => {
    if (!open) return;

    if (editingSpecialist) {
      console.log('🔍 SpecialistModal - Datos del especialista:', {
        deliveryMethods: (editingSpecialist as any).deliveryMethods,
        modalities: editingSpecialist.modalities,
        contacts: (editingSpecialist as any).contacts
      });
      
      const mappedModalityCodes = (editingSpecialist as any).deliveryMethods ? 
        (editingSpecialist as any).deliveryMethods.split(',').map((code: string) => code.trim()) : 
        editingSpecialist.modalities?.map((m: any) => m.code || m.name) || [];
      
      console.log('🔍 SpecialistModal - Modalidades mapeadas:', {
        mappedModalityCodes: mappedModalityCodes,
        length: mappedModalityCodes.length
      });
      
      setFormData({
        fullName: editingSpecialist.fullName,
        productArea: editingSpecialist.productArea || '',
        coverageArea: (editingSpecialist as any).coverageArea || null, // ✅ NUEVO: Cargar área de cobertura
        notes: editingSpecialist.notes || '',
        isActive: editingSpecialist.active ?? true,
        modalityCodes: mappedModalityCodes,
        specialtyNames: editingSpecialist.specialties?.map((s: any) => s.name) || [],
        specialties: editingSpecialist.specialties?.map((s: any) => ({
          specialtyId: s.specialtyId,
          name: s.name,
          isNew: false
        })) || [],
        contacts: (editingSpecialist as any).contacts || [],
        addresses: [], // si tienes mapeo desde BE, aquí
        temporaryAddresses: ((editingSpecialist as any).temporaryAddresses || []).map((ta: TemporaryAddressDTO) => ({
          id: ta.id,
          direccion: ta.direccion,
          horario: ta.horario || '',
          notas: ta.notas || ''
        })), // ✅ NUEVO: Cargar direcciones temporales
      });
    } else {
      setFormData({
        fullName: '',
        productArea: '',
        coverageArea: null, // ✅ NUEVO: Sin área de cobertura
        notes: '',
        isActive: true,
        modalityCodes: [],
        specialtyNames: [],
        specialties: [],
        contacts: [],
        addresses: [],
        temporaryAddresses: [], // ✅ NUEVO: Sin direcciones temporales
      });
    }
  }, [open, editingSpecialist]);

  // Utilidades
  const normalizeCode = (code: string): string =>
    code.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');

  const handleFormChange = (field: keyof SpecialistFormData, value: any) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  // Estados → Municipios → Ciudades
  const handleStateChange = (state: StateDTO | null, addressIndex: number) => {
    const updated = [...formData.addresses];
    updated[addressIndex] = {
      ...updated[addressIndex],
      cveEnt: (state as any)?.stateCode || '',
      cveMun: '',
      cveLoc: '',
      settlementId: undefined,
    };
    setFormData(prev => ({ ...prev, addresses: updated }));
    setMunicipalities([]);
    setCities([]);
  };

  const handleMunicipalitySearch = async (searchTerm: string, addressIndex: number) => {
    const address = formData.addresses[addressIndex];
    if (!address.cveEnt || !jwtToken) return;
    setAddressLoading(prev => ({ ...prev, municipalities: true }));
    try {
      const response = await searchMunicipalitiesByState(address.cveEnt, searchTerm);
      if (response.ok) setMunicipalities(response.data.content || []);
    } catch (e) {
      console.error('❌ Error buscando municipios:', e);
    } finally {
      setAddressLoading(prev => ({ ...prev, municipalities: false }));
    }
  };

  const handleCitySearch = async (searchTerm: string, addressIndex: number) => {
    const address = formData.addresses[addressIndex];
    if (!address.cveEnt || !jwtToken) return;
    setAddressLoading(prev => ({ ...prev, cities: true }));
    try {
      const municipalityCode = address.cveMun || '';
      const response = await searchCitiesByStateAndMunicipality(address.cveEnt, municipalityCode, searchTerm);
      if (response.ok) setCities(response.data.content || []);
    } catch (e) {
      console.error('❌ Error buscando ciudades:', e);
    } finally {
      setAddressLoading(prev => ({ ...prev, cities: false }));
    }
  };

  // Direcciones
  const addAddress = () => {
    const newAddress: AddressFormData = {
      cveEnt: '',
      cveMun: '',
      cveLoc: '',
      cp: '',
      settlementId: undefined,
      calle: '',
      numExterior: '',
      numInterior: '',
      referencias: '',
      isPrimary: formData.addresses.length === 0,
    };
    setFormData(prev => ({ ...prev, addresses: [...prev.addresses, newAddress] }));
  };

  const removeAddress = (index: number) =>
    setFormData(prev => ({ ...prev, addresses: prev.addresses.filter((_, i) => i !== index) }));

  const updateAddress = (index: number, field: keyof AddressFormData, value: any) =>
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) => (i === index ? { ...addr, [field]: value } : addr)),
    }));

  const setPrimaryAddress = (index: number) =>
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) => ({ ...addr, isPrimary: i === index })),
    }));

  // ✅ NUEVO: Funciones para manejar direcciones temporales
  const addTemporaryAddress = () => {
    const newTempAddress: TemporaryAddressFormData = {
      direccion: '',
      horario: '',
      notas: ''
    };
    setFormData(prev => ({ 
      ...prev, 
      temporaryAddresses: [...prev.temporaryAddresses, newTempAddress] 
    }));
  };

  const removeTemporaryAddress = (index: number) =>
    setFormData(prev => ({ 
      ...prev, 
      temporaryAddresses: prev.temporaryAddresses.filter((_, i) => i !== index) 
    }));

  const updateTemporaryAddress = (index: number, field: keyof TemporaryAddressFormData, value: any) =>
    setFormData(prev => ({
      ...prev,
      temporaryAddresses: prev.temporaryAddresses.map((addr, i) => 
        (i === index ? { ...addr, [field]: value } : addr)
      ),
    }));

  // Guardado
  const handleSave = async () => {
    console.log('💾 Guardando desde modal - formData completo:', {
      ...formData,
      specialtyNames: formData.specialtyNames,
      specialtyNamesCount: formData.specialtyNames.length
    });
    await onSave(formData);
    onClose();
  };

  // Manejo de tabs
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const loadStatusHistory = async (page = 0) => {
    if (!editingSpecialist?.specialistId) return;
    try {
      setHistoryLoading(true);
      setHistoryError('');
      const res = await getSpecialistStatusHistory(editingSpecialist.specialistId, page, 10);
      setStatusHistory(res.history || []);
      setHistoryPage(res.currentPage ?? page);
      setHistoryTotal(res.totalItems ?? 0);
    } catch (error: any) {
      setStatusHistory([]);
      setHistoryError(error?.message || 'No se pudo cargar el historial de estatus');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !editingSpecialist?.specialistId) {
      setStatusHistory([]);
      setHistoryPage(0);
      setHistoryTotal(0);
      setHistoryError('');
      return;
    }
    if (tabValue === 3) {
      void loadStatusHistory(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingSpecialist?.specialistId, tabValue]);

  const openStatusReasonDialog = (nextActive: boolean) => {
    setPendingStatusActive(nextActive);
    setStatusReason('');
    setStatusError('');
    setStatusReasonDialogOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!editingSpecialist?.specialistId || pendingStatusActive == null) return;
    const reason = statusReason.trim();
    if (!reason) {
      setStatusError('El motivo es obligatorio');
      return;
    }
    if (reason.length > 500) {
      setStatusError('El motivo no puede superar 500 caracteres');
      return;
    }
    try {
      setStatusSaving(true);
      setStatusError('');
      const updated = await updateSpecialistStatus(editingSpecialist.specialistId, {
        isActive: pendingStatusActive,
        reason,
      });
      const normalized: Specialist = {
        ...editingSpecialist,
        ...updated,
        active: (updated as any).active ?? (updated as any).isActive ?? pendingStatusActive,
        isActive: (updated as any).isActive ?? (updated as any).active ?? pendingStatusActive,
      };
      setFormData((prev) => ({ ...prev, isActive: resolveSpecialistActive(normalized) }));
      onStatusChanged?.(normalized);
      setStatusReasonDialogOpen(false);
      setPendingStatusActive(null);
      setStatusReason('');
      if (tabValue === 3) {
        void loadStatusHistory(historyPage);
      }
    } catch (error: any) {
      setStatusError(error?.message || 'No se pudo cambiar el estatus');
    } finally {
      setStatusSaving(false);
    }
  };

  // Render
  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth disablePortal={false} disableScrollLock={true}>
      <DialogTitle>{editingSpecialist ? 'Editar Especialista' : 'Nuevo Especialista'}</DialogTitle>
      <DialogContent>
        {catalogsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Cargando catálogos...</Typography>
          </Box>
        ) : (
          <>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="specialist tabs" 
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  📋 Información Básica
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  📞 Medios de Contacto
                  {formData.contacts.length > 0 && (
                    <Chip label={formData.contacts.length} size="small" color="primary" />
                  )}
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  🏠 Direcciones
                  {(formData.addresses.length > 0 || formData.temporaryAddresses.length > 0) && (
                    <Chip 
                      label={formData.addresses.length + formData.temporaryAddresses.length} 
                      size="small" 
                      color="primary" 
                    />
                  )}
                </Box>
              } />
              {editingSpecialist && (
                <Tab label="Historial de estatus" />
              )}
            </Tabs>

            {/* Tab 1: Información Básica */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={2}>
                {editingSpecialist && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Estatus
                          </Typography>
                          <Chip
                            label={currentActive ? 'Activo' : 'Inactivo'}
                            color={currentActive ? 'success' : 'error'}
                            size="small"
                          />
                          {canChangeStatus && (
                            <Button
                              size="small"
                              variant="outlined"
                              color={currentActive ? 'error' : 'success'}
                              onClick={() => openStatusReasonDialog(!currentActive)}
                            >
                              {currentActive ? 'Desactivar' : 'Activar'}
                            </Button>
                          )}
                        </Box>
                        {editingSpecialist.statusChangeReason && (
                          <Typography variant="body2" color="text.secondary">
                            Motivo: {editingSpecialist.statusChangeReason}
                          </Typography>
                        )}
                        {(editingSpecialist.statusChangedAt || editingSpecialist.statusChangedBy) && (
                          <Typography variant="caption" color="text.secondary">
                            {editingSpecialist.statusChangedAt
                              ? `Actualizado el ${new Date(editingSpecialist.statusChangedAt).toLocaleString('es-MX')}`
                              : ''}
                            {editingSpecialist.statusChangedBy
                              ? ` · Por ${
                                  editingSpecialist.statusChangedBy.fullName ||
                                  editingSpecialist.statusChangedBy.username ||
                                  `ID ${editingSpecialist.statusChangedBy.id}`
                                }`
                              : ''}
                          </Typography>
                        )}
                        {!canChangeStatus && (
                          <Typography variant="caption" color="text.secondary">
                            Solo ADMIN o SUPERVISOR pueden cambiar el estatus.
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                fullWidth
                label="Nombre Completo"
                value={formData.fullName}
                onChange={(e) => handleFormChange('fullName', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Área de Producto</InputLabel>
                <Select
                  value={formData.productArea}
                  onChange={(e) => handleFormChange('productArea', e.target.value)}
                  label="Área de Producto"
                >
                  {(catalogs.PRODUCT || []).map((item: any) => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ✅ NUEVO: Área de Cobertura Geográfica */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Área de Cobertura Geográfica</InputLabel>
                <Select
                  value={formData.coverageArea?.coverageAreaId || ''}
                  onChange={(e) => {
                    const areaId = e.target.value;
                    const selectedArea = coverageAreas.find(a => a.coverageAreaId === areaId);
                    handleFormChange('coverageArea', selectedArea || null);
                  }}
                  label="Área de Cobertura Geográfica"
                >
                  <MenuItem value="">Seleccionar área...</MenuItem>
                  {coverageAreas.map((area: any) => (
                    <MenuItem key={area.coverageAreaId} value={area.coverageAreaId}>
                      {area.description} ({area.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Especialidades */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Autocomplete
                    freeSolo
                    fullWidth
                    options={(() => {
                      const opts = Array.isArray(specialties) ? specialties.map((s: any) => s.name || s) : [];
                      console.log('🎯 Autocomplete options:', { count: opts.length, options: opts });
                      return opts;
                    })()}
                    value={''}
                    onChange={(_, newValue: any) => {
                      if (newValue && typeof newValue === 'string' && newValue.trim()) {
                        const trimmedValue = newValue.trim();
                        if (!formData.specialtyNames.includes(trimmedValue)) {
                          console.log('➕ Agregando especialidad:', trimmedValue);
                          
                          // Buscar en el catálogo si ya existe
                          const existingFromCatalog = specialties.find(
                            (s: any) => s.name && s.name.toLowerCase() === trimmedValue.toLowerCase()
                          );
                          
                          // Verificar si ya existe en las especialidades actuales del formData
                          const existingFromCurrent = formData.specialties.find(
                            (s) => s.name.toLowerCase() === trimmedValue.toLowerCase()
                          );
                          
                          const specialtyItem: SpecialtyFormItem = {
                            specialtyId: existingFromCatalog?.specialtyId || existingFromCurrent?.specialtyId || null,
                            name: trimmedValue,
                            isNew: !existingFromCatalog && !existingFromCurrent
                          };
                          
                          console.log('📝 Especialidad agregada:', {
                            name: trimmedValue,
                            specialtyId: specialtyItem.specialtyId,
                            isNew: specialtyItem.isNew,
                            foundInCatalog: !!existingFromCatalog,
                            foundInCurrent: !!existingFromCurrent
                          });
                          
                          // Actualizar ambos arrays
                          handleFormChange('specialtyNames', [...formData.specialtyNames, trimmedValue]);
                          handleFormChange('specialties', [...formData.specialties, specialtyItem]);
                        } else {
                          console.log('⚠️ Especialidad ya existe');
                        }
                        // Limpiar el input
                        (document.activeElement as HTMLElement)?.blur();
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Agregar Especialidad"
                        placeholder="Escriba o seleccione una especialidad"
                        helperText="Escriba una nueva especialidad o seleccione de la lista"
                      />
                    )}
                  />
                </Box>
                
                {/* Lista de especialidades agregadas */}
                {formData.specialtyNames.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Especialidades agregadas ({formData.specialtyNames.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.specialtyNames.map((specialtyName, index) => (
                        <Chip
                          key={index}
                          label={specialtyName}
                          onDelete={() => {
                            console.log('🗑️ Eliminando especialidad:', specialtyName);
                            const newSpecialtyNames = formData.specialtyNames.filter((_, i) => i !== index);
                            const newSpecialties = formData.specialties.filter((_, i) => i !== index);
                            handleFormChange('specialtyNames', newSpecialtyNames);
                            handleFormChange('specialties', newSpecialties);
                          }}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Modalidades */}
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  Modalidades
                </FormLabel>
                <FormGroup>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(catalogs.DELIVERY_METHOD || []).map((item: any) => {
                      const normalizedCode = normalizeCode(item.code);
                      const isChecked = formData.modalityCodes.includes(normalizedCode);
                      
                      console.log('🔍 Comparando modalidad:', {
                        itemCode: item.code,
                        normalizedCode: normalizedCode,
                        formDataModalityCodes: formData.modalityCodes,
                        isChecked: isChecked
                      });
                      
                      return (
                        <FormControlLabel
                          key={item.code}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleFormChange('modalityCodes', [...formData.modalityCodes, normalizedCode]);
                                } else {
                                  handleFormChange('modalityCodes', formData.modalityCodes.filter(code => code !== normalizedCode));
                                }
                              }}
                              size="small"
                            />
                          }
                          label={item.description}
                          sx={{ margin: 0 }}
                        />
                      );
                    })}
                  </Box>
                </FormGroup>
              </FormControl>
            </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 2: Medios de Contacto */}
            <TabPanel value={tabValue} index={1}>
              <SpecialistContactForm
                contacts={formData.contacts}
                onChange={(contacts) => handleFormChange('contacts', contacts)}
                contactTypes={catalogs.CONTACT_TYPE || []}
              />
            </TabPanel>

            {/* Tab 3: Direcciones */}
            <TabPanel value={tabValue} index={2}>
              {/* Direcciones Estructuradas */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Domicilios/Consultorios Estructurados</Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addAddress} size="small">
                    Agregar Domicilio
                  </Button>
                </Box>

                {formData.addresses.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: 'grey.50' }}>
                    <Typography color="text.secondary">
                      No hay domicilios registrados. Haz clic en "Agregar Domicilio" para comenzar.
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {formData.addresses.map((address, index) => (
                      <Paper key={index} sx={{ p: 2, border: address.isPrimary ? '2px solid' : '1px solid', borderColor: address.isPrimary ? 'primary.main' : 'grey.300' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              Domicilio {index + 1}
                            </Typography>
                            {address.isPrimary && <Chip label="Principal" color="primary" size="small" />}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {!address.isPrimary && (
                              <Button size="small" onClick={() => setPrimaryAddress(index)} color="primary">
                                Hacer Principal
                              </Button>
                            )}
                            <IconButton size="small" onClick={() => removeAddress(index)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Calle"
                              value={address.calle}
                              onChange={(e) => updateAddress(index, 'calle', e.target.value)}
                              placeholder="Av. Insurgentes Sur"
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="Número Exterior"
                              value={address.numExterior}
                              onChange={(e) => updateAddress(index, 'numExterior', e.target.value)}
                              placeholder="123"
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="Número Interior"
                              value={address.numInterior}
                              onChange={(e) => updateAddress(index, 'numInterior', e.target.value)}
                              placeholder="A"
                            />
                          </Grid>

                          {/* Estado */}
                          <Grid item xs={12} sm={4}>
                            <AutocompleteField
                              label="Estado"
                              placeholder="Seleccionar estado..."
                              value={(states as any[]).find((s: any) => s.stateCode === address.cveEnt) || null}
                              options={states as any}
                              loading={addressLoading.states}
                              required
                              displayField="stateName"
                              onInputChange={() => {}}
                              onChange={(newValue) => handleStateChange(newValue as any, index)}
                            />
                          </Grid>

                          {/* Municipio */}
                          <Grid item xs={12} sm={4}>
                            <AutocompleteField
                              label="Municipio"
                              placeholder="Buscar municipio..."
                              value={(municipalities as any[]).find((m: any) => m.municipalityCode === address.cveMun) || null}
                              options={municipalities as any}
                              loading={addressLoading.municipalities}
                              disabled={!address.cveEnt}
                              required
                              displayField="municipalityName"
                              onInputChange={(searchTerm) => {
                                if ((searchTerm || '').length >= 3) handleMunicipalitySearch(searchTerm, index);
                              }}
                              onChange={(newValue) => {
                                updateAddress(index, 'cveMun', (newValue as any)?.municipalityCode || '');
                                updateAddress(index, 'cveLoc', '');
                              }}
                            />
                          </Grid>

                          {/* Ciudad/Localidad */}
                          <Grid item xs={12} sm={4}>
                            <AutocompleteField
                              label="Ciudad/Localidad"
                              placeholder="Buscar ciudad..."
                              value={(cities as any[]).find((c: any) => c.cityName === address.cveLoc) || null}
                              options={cities as any}
                              loading={addressLoading.cities}
                              disabled={!address.cveEnt || !address.cveMun}
                              required
                              displayField="cityName"
                              onInputChange={(searchTerm) => {
                                if ((searchTerm || '').length >= 3) handleCitySearch(searchTerm, index);
                              }}
                              onChange={(newValue) => updateAddress(index, 'cveLoc', (newValue as any)?.cityName || '')}
                            />
                          </Grid>

                          {/* Código Postal */}
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Código Postal"
                              value={address.cp}
                              onChange={(e) => updateAddress(index, 'cp', e.target.value)}
                              placeholder="01000"
                              inputProps={{ maxLength: 5 }}
                              required
                            />
                          </Grid>

                          {/* Referencias */}
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Referencias"
                              value={address.referencias}
                              onChange={(e) => updateAddress(index, 'referencias', e.target.value)}
                              placeholder="Edificio Torre Médica, 3er piso"
                              multiline
                              rows={2}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Direcciones */}
              <Box sx={{ mt: 4, pt: 4, borderTop: '2px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="warning" />
                    <Typography variant="h6">Direcciones (Texto Libre)</Typography>
                    {formData.temporaryAddresses.length > 0 && (
                      <Chip 
                        label={formData.temporaryAddresses.length} 
                        size="small" 
                        color="warning" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />} 
                    onClick={addTemporaryAddress} 
                    size="small"
                    color="warning"
                  >
                    Agregar Dirección
                  </Button>
                </Box>
                
                <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                    Estas direcciones están en formato de texto libre y pueden ser procesadas posteriormente para convertirlas en direcciones estructuradas.
                  </Typography>
                </Paper>

                {formData.temporaryAddresses.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: 'grey.50' }}>
                    <Typography color="text.secondary">
                      No hay direcciones registradas. Haz clic en "Agregar Dirección" para comenzar.
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {formData.temporaryAddresses.map((tempAddr, index) => (
                      <Paper 
                        key={index} 
                        sx={{ 
                          p: 2, 
                          border: '1px dashed', 
                          borderColor: 'warning.main',
                          backgroundColor: 'rgba(255, 152, 0, 0.05)',
                          borderRadius: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                              Dirección {index + 1}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => removeTemporaryAddress(index)} 
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Dirección *"
                              value={tempAddr.direccion}
                              onChange={(e) => updateTemporaryAddress(index, 'direccion', e.target.value)}
                              placeholder="Ej: ANZURES: Milton #28 Col. Anzures 11590"
                              required
                              multiline
                              rows={2}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Horario de Atención"
                              value={tempAddr.horario || ''}
                              onChange={(e) => updateTemporaryAddress(index, 'horario', e.target.value)}
                              placeholder="Ej: Jueves de 16: a 20: hrs. Sábado 08: a 14: hrs."
                              multiline
                              rows={2}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Notas"
                              value={tempAddr.notas || ''}
                              onChange={(e) => updateTemporaryAddress(index, 'notas', e.target.value)}
                              placeholder="Ej: Previa cita, tiene que reservar consultorio"
                              multiline
                              rows={2}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            </TabPanel>

            {editingSpecialist && (
              <TabPanel value={tabValue} index={3}>
                <Stack spacing={2}>
                  {historyError && <Alert severity="error">{historyError}</Alert>}
                  {historyLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Cargando historial…</Typography>
                    </Box>
                  ) : statusHistory.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No hay cambios de estatus registrados.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Fecha</TableCell>
                            <TableCell>De → A</TableCell>
                            <TableCell>Motivo</TableCell>
                            <TableCell>Usuario</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {statusHistory.map((entry) => (
                            <TableRow key={entry.id}>
                              <TableCell>
                                {entry.changedAt
                                  ? new Date(entry.changedAt).toLocaleString('es-MX')
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                {entry.previousActive ? 'Activo' : 'Inactivo'} →{' '}
                                {entry.newActive ? 'Activo' : 'Inactivo'}
                              </TableCell>
                              <TableCell>{entry.reason || '—'}</TableCell>
                              <TableCell>
                                {entry.changedBy?.fullName ||
                                  entry.changedBy?.username ||
                                  (entry.changedBy?.id != null ? `ID ${entry.changedBy.id}` : '—')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <TablePagination
                        component="div"
                        count={historyTotal}
                        page={historyPage}
                        onPageChange={(_, page) => void loadStatusHistory(page)}
                        rowsPerPage={10}
                        rowsPerPageOptions={[10]}
                        labelRowsPerPage="Filas"
                      />
                    </TableContainer>
                  )}
                </Stack>
              </TabPanel>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || !formData.fullName.trim()} startIcon={loading ? <CircularProgress size={20} /> : null}>
          {loading ? 'Guardando...' : editingSpecialist ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={statusReasonDialogOpen}
      onClose={() => {
        if (statusSaving) return;
        setStatusReasonDialogOpen(false);
        setPendingStatusActive(null);
        setStatusReason('');
        setStatusError('');
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {pendingStatusActive ? 'Activar especialista' : 'Desactivar especialista'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {statusError && <Alert severity="error">{statusError}</Alert>}
          <Typography variant="body2">
            Indica el motivo del cambio de estatus (obligatorio, máx. 500 caracteres).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Motivo"
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value.slice(0, 500))}
            helperText={`${statusReason.length}/500`}
            disabled={statusSaving}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setStatusReasonDialogOpen(false);
            setPendingStatusActive(null);
            setStatusReason('');
            setStatusError('');
          }}
          disabled={statusSaving}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color={pendingStatusActive ? 'success' : 'error'}
          onClick={() => void handleConfirmStatusChange()}
          disabled={statusSaving || !statusReason.trim()}
        >
          {statusSaving ? 'Guardando…' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default SpecialistModal;
